import { useCallback, useEffect, useRef, useState } from 'react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { loadAttachmentDraft } from '../../../lib/composer/attachmentDrafts.js';
import { apiFetch } from '../../../lib/api.js';
import { setRunMessages } from '../../../lib/agentRunStore.js';

function restoreComposerFocus() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('apolo:focusComposer'));
  } catch {
    /* never break the resume flow on a dispatch failure */
  }
}

// E9-T01 — o turno pausado no Spec Gate sobrevive ao reload: snapshot SLIM
// (sem attachments — podem ser MB de base64) em localStorage por sessão. O
// card de spec_review já persiste nas mensagens; sem isto o Accept pós-reload
// era um clique morto (ref zerada). Best-effort: storage cheio ⇒ ref-only.
const PENDING_SPEC_LS = 'apolo:pendingSpecTurn:v1';
function persistPendingSpec(pending) {
  try {
    if (!pending?.sessionId) return;
    const { attachments: _omit, ...slim } = pending;
    window.localStorage?.setItem(`${PENDING_SPEC_LS}:${pending.sessionId}`, JSON.stringify(slim));
  } catch { /* best-effort */ }
}
function clearPendingSpecStore(sessionId) {
  try { if (sessionId) window.localStorage?.removeItem(`${PENDING_SPEC_LS}:${sessionId}`); } catch { /* noop */ }
}
function loadPendingSpec(sessionId) {
  try {
    const raw = sessionId ? window.localStorage?.getItem(`${PENDING_SPEC_LS}:${sessionId}`) : null;
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.sessionId === sessionId && parsed.turnToken ? parsed : null;
  } catch { return null; }
}

/**
 * Manages the dispatch wiring around the agent turn lifecycle:
 * - Spec Gate refs (pendingSpecTurnRef, specGenAbortRef)
 * - runAgentRef / sessionIdRef (always point to current render)
 * - Spec accept/skip event listeners (mount-once)
 * - Session-switch cleanup (drops pending spec turn on session change)
 * - Auto-first-message (fires pendingFirstMessage once adapters load)
 *
 * @param {{ sessionId: string|undefined, adaptersLoading: boolean }} opts
 */
export function useTurnDispatch({ sessionId, adaptersLoading }) {
  const pendingSpecTurnRef = useRef(null);
  const [generatingSpec, setGeneratingSpec] = useState(false);
  const specGenAbortRef = useRef(null);
  const runAgentRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const pendingFirstMessage = useAgentStore((s) => s.code.pendingFirstMessage);
  const clearPendingFirstMessage = useAgentStore((s) => s.clearPendingFirstMessage);
  const firstMsgFiredForRef = useRef(null);
  const initialIntent = useAgentStore((s) => s.code.sessions?.find((item) => item.id === sessionId)?.client_intent_id);

  useEffect(() => {
    const store = useAgentStore.getState?.();
    const session = store?.code?.sessions?.find((item) => item.id === sessionId);
    if (!session?.client_intent_id || store.code.pendingFirstMessage?.[sessionId]) return;
    let live = true;
    apiFetch(`/api/code/sessions/${sessionId}/initial-delivery`).then(async (response) => {
      if (!live || !response.ok) return;
      const delivery = response.data?.delivery;
      if (delivery?.state === 'claimed') {
        setRunMessages(sessionId, (messages) => messages.some((m) => m.id === 'initial-delivery-recovery') ? messages : [...messages, { id: 'initial-delivery-recovery', role: 'system', content: 'A primeira mensagem já foi recebida. Consulte a atividade e os arquivos antes de repetir a execução; nenhum efeito será reenviado automaticamente.' }]);
      }
      if (delivery?.state === 'accepted') {
        const attachments = delivery.draft_scope ? await loadAttachmentDraft(delivery.draft_scope) : [];
        if (delivery.attachment_count && attachments.length !== delivery.attachment_count) throw new Error('Os anexos originais não estão disponíveis neste dispositivo. Recupere-os antes de reenviar.');
        if (live) store.setPendingFirstMessage(sessionId, delivery.content, attachments, true);
      }
    }).catch((error) => {
      if (live) setRunMessages(sessionId, (messages) => [...messages.filter((m) => m.id !== 'initial-delivery-recovery'), { id: 'initial-delivery-recovery', role: 'system', content: error.message || 'Não foi possível recuperar a primeira mensagem.' }]);
    });
    return () => { live = false; };
  }, [sessionId, initialIntent]);

  // Spec accept/skip listeners (mount-once). Handlers read only refs
  // (runAgentRef/sessionIdRef/pendingSpecTurnRef), reassigned every render,
  // so no deps are needed and there's no stale-closure risk.
  useEffect(() => {
    function resolveArtifact(token, sid, resolved) {
      setRunMessages(sid, (m) =>
        m.map((msg) =>
          msg.tool_name === 'spec_review' && msg.metadata?.turnToken === token
            ? { ...msg, metadata: { ...msg.metadata, resolved } }
            : msg,
        ),
      );
    }
    function onSpecAccept(evt) {
      const pending = pendingSpecTurnRef.current;
      const detail = evt.detail || {};
      if (!pending || detail.turnToken !== pending.turnToken) return;
      if (detail.sessionId !== sessionIdRef.current || pending.sessionId !== sessionIdRef.current) return;
      pendingSpecTurnRef.current = null;
      clearPendingSpecStore(pending.sessionId); // E9-T01 — consumido: some do storage
      resolveArtifact(detail.turnToken, sessionIdRef.current, 'accepted');
      restoreComposerFocus();
      runAgentRef.current?.({ ...pending, spec: detail.spec });
    }
    function onSpecSkip(evt) {
      const pending = pendingSpecTurnRef.current;
      const detail = evt.detail || {};
      if (!pending || detail.turnToken !== pending.turnToken) return;
      if (detail.sessionId !== sessionIdRef.current || pending.sessionId !== sessionIdRef.current) return;
      pendingSpecTurnRef.current = null;
      clearPendingSpecStore(pending.sessionId); // E9-T01
      resolveArtifact(detail.turnToken, sessionIdRef.current, 'skipped');
      restoreComposerFocus();
      runAgentRef.current?.({ ...pending });
    }
    window.addEventListener('apolo:spec:accept', onSpecAccept);
    window.addEventListener('apolo:spec:skip', onSpecSkip);
    return () => {
      window.removeEventListener('apolo:spec:accept', onSpecAccept);
      window.removeEventListener('apolo:spec:skip', onSpecSkip);
    };
  }, []);

  // E9-T01 — hidratação pós-reload: remount com a MESMA sessão (reload/reopen)
  // recupera o turno pausado do storage; o card de spec_review persistido volta
  // a ter um Accept/Skip vivo. Sessão trocada cai no cleanup abaixo (supersede).
  useEffect(() => {
    if (!pendingSpecTurnRef.current) {
      pendingSpecTurnRef.current = loadPendingSpec(sessionId);
    }
  }, [sessionId]);

  // Session-switch cleanup: drop pending spec turn from previous session,
  // mark its artifact superseded, cancel in-flight spec generation.
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;
    if (prev === sessionId) return;
    const pending = pendingSpecTurnRef.current;
    if (pending) {
      setRunMessages(pending.sessionId, (m) =>
        m.map((msg) =>
          msg.tool_name === 'spec_review' &&
          msg.metadata?.turnToken === pending.turnToken &&
          !msg.metadata?.resolved
            ? { ...msg, metadata: { ...msg.metadata, resolved: 'superseded' } }
            : msg,
        ),
      );
      pendingSpecTurnRef.current = null;
      clearPendingSpecStore(pending.sessionId); // E9-T01 — superseded: some do storage
    }
    try { specGenAbortRef.current?.abort(); } catch { /* noop */ }
    specGenAbortRef.current = null;
    setGeneratingSpec(false);
  }, [sessionId]);

  // Auto-fire the first pending message once adapters are loaded.
  // Returns a callback the caller invokes with handleSend — keeps the
  // effect pure (no dependency on the full handleSend closure).
  const handleSendRef = useRef(null);
  useEffect(() => {
    if (adaptersLoading || !handleSendRef.current) return;
    // #16 — pendingFirstMessage é um mapa POR sessionId; pega só a entrada DESTA sessão.
    const pending = sessionId ? pendingFirstMessage?.[sessionId] : null;
    if (
      pending &&
      pending.text &&
      firstMsgFiredForRef.current !== sessionId
    ) {
      firstMsgFiredForRef.current = sessionId;
      const text = pending.text;
      // chat-hardening S2 #2: encaminha os anexos da home do Chat pro 1º turno
      // (handleSend já os serializa via buildUserContent). null quando não há.
      const attachments = pending.attachments || undefined;
      const dispatch = async () => {
        let initialMessageId;
        let acceptedText = text;
        if (pending.durable) {
          const claim = await apiFetch(`/api/code/sessions/${sessionId}/initial-delivery/claim`, { method: 'POST' });
          if (!claim.ok) {
            firstMsgFiredForRef.current = null;
            window.dispatchEvent(new CustomEvent('apolo:toast', { detail: { kind: 'error', message: 'Primeira mensagem salva. Reabra o chat para retomar o envio.' } }));
            return;
          }
          if (!claim.data?.claimed) { clearPendingFirstMessage(sessionId); return; }
          initialMessageId = claim.data.message_id;
          acceptedText = claim.data.content || text;
        }
        clearPendingFirstMessage(sessionId);
        await handleSendRef.current?.({ text: acceptedText, attachments, initialMessageId });
        if (pending.durable) await apiFetch(`/api/code/sessions/${sessionId}/initial-delivery/complete`, { method: 'POST' });
      };
      dispatch().catch((error) => {
        setRunMessages(sessionId, (messages) => [...messages.filter((m) => m.id !== 'initial-delivery-recovery'), {id:'initial-delivery-recovery',role:'system',content:`A primeira mensagem permanece registrada. Consulte Atividade antes de repetir. ${error.message || 'Não foi possível confirmar a entrega.'}`}]);
      });
      // Auto-título (recents-claude-style): nome heurístico + refino LLM em
      // background quando a sessão ainda tem nome genérico. Fire-and-forget —
      // nunca bloqueia nem quebra o 1º turno.
      useAgentStore.getState?.()?.autoTitleFromFirstMessage?.(sessionId, text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFirstMessage, sessionId, adaptersLoading]);

  // E9-T01 — setter que mantém ref+storage em sincronia (o SessionWorkspace usa
  // este em vez de atribuir a ref direto; null = descarte explícito do pendente).
  const setPendingSpecTurn = useCallback((p) => {
    pendingSpecTurnRef.current = p;
    if (p) persistPendingSpec(p);
    else clearPendingSpecStore(sessionIdRef.current);
  }, []);

  return {
    pendingSpecTurnRef,
    setPendingSpecTurn,
    generatingSpec,
    setGeneratingSpec,
    specGenAbortRef,
    runAgentRef,
    sessionIdRef,
    handleSendRef,
  };
}
